/**
 * World-space distance (metres) the floorplan camera trucks per arrow key press.
 * Configurable in the app menu; this is the default.
 */
export const defaultFloorplanPanStep = 1

/**
 * Relative change applied to the orthographic zoom per plus or minus press.
 * Configurable in the app menu; this is the default.
 * Plus multiplies by this, minus divides by it.
 */
export const defaultFloorplanZoomFactor = 1.5

/**
 * Plus zooms the floorplan in, minus zooms it out.
 *
 * react-hotkeys-hook splits combinations on "+" and maps the "+" / "-"
 * characters onto the German key codes BracketRight and Slash. `add` /
 * `subtract` are the numpad keys. `shift+equal` and `minus` are the same
 * characters on a US layout.
 */
export const floorplanZoomInHotkeys = ['bracketright', 'add', 'shift+equal']
export const floorplanZoomOutHotkeys = ['slash', 'subtract', 'minus']

const physicalArrowCodes = new Set(['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'])

/** Main-keyboard arrows. Numpad navigation reports `Numpad*` in `event.code`. */
export const isPhysicalArrowKey = (event: KeyboardEvent): boolean =>
  physicalArrowCodes.has(event.code)

/**
 * Focused widgets that already use these keys. Floorplan panning and zoom must leave
 * those events alone. react-hotkeys-hook only ignores form fields by default.
 */
export const arrowKeyOwnerSelectors = [
  '.MuiDataGrid-root',
  '[role="grid"]',
  '[role="gridcell"]',
  '[role="columnheader"]',
  'table',
  '[role="listbox"]',
  '[role="menu"]',
  '[role="menuitem"]',
  '[role="tablist"]',
  '[role="tab"]',
  '[role="slider"]',
  '[role="tree"]',
  '[role="treeitem"]',
  '[role="combobox"]',
  '[role="option"]',
  '[role="dialog"]',
]

export const isArrowKeyOwnedByFocusedWidget = (event: KeyboardEvent): boolean => {
  const target = event.target
  if (target == null || typeof (target as Element).closest !== 'function') return false
  return arrowKeyOwnerSelectors.some(selector => (target as Element).closest(selector) !== null)
}

/**
 * Truck offset so the view moves in the arrow's direction, like scrolling a map.
 * camera-controls applies +x along camera-right and +y opposite camera-up,
 * so the camera moves with the arrow and the model slides against it.
 */
export const floorplanPanTruck = (code: string, step: number): { x: number; y: number } | null => {
  if (!Number.isFinite(step) || step === 0) return null
  switch (code) {
    case 'ArrowLeft':
      return { x: -step, y: 0 }
    case 'ArrowRight':
      return { x: step, y: 0 }
    case 'ArrowUp':
      return { x: 0, y: -step }
    case 'ArrowDown':
      return { x: 0, y: step }
    default:
      return null
  }
}

/**
 * Next orthographic zoom. Plus multiplies, minus divides, so each press is the
 * same relative step. Returns null when the inputs cannot produce a zoom.
 */
export const floorplanZoomTarget = (
  currentZoom: number,
  direction: 1 | -1,
  factor: number = defaultFloorplanZoomFactor,
): number | null => {
  if (!Number.isFinite(currentZoom) || currentZoom <= 0) return null
  if (!Number.isFinite(factor) || factor <= 1) return null
  return direction > 0 ? currentZoom * factor : currentZoom / factor
}
